<template>
  <div class="gokul-page">
    <!-- Password Protection Screen -->
    <div v-if="!isAuthenticated" class="password-screen">
      <div class="container">
        <div class="password-container">
          <div class="diwali-icon">🪔</div>
          <h2>Happy Diwali!</h2>
          <p>Enter the password to access this special page</p>
          <div class="password-clue">
            <p class="clue-text">💡 Clue: 3 possible passwords. <br># we both call each other with this name <br>or<br> # Your special name for me — p_ _ _i <br>or<br> # my special name for you — p_ _ _ _ _ h <br>🤔🤔🤔</p>
          </div>
          <div class="password-input-container">
            <input
              v-model="password"
              type="password"
              placeholder="Enter password"
              @keyup.enter="checkPassword"
              class="password-input"
            />
            <button @click="checkPassword" class="btn btn-primary">Enter</button>
          </div>
          <p v-if="errorMessage" class="error-message">{{ errorMessage }}</p>
        </div>
      </div>
    </div>

    <!-- Main Diwali Content -->
    <div v-else class="diwali-content">
      <div class="container">
        <!-- Hero Section -->
        <section class="diwali-hero">
          <div class="diwali-title-container">
            <h1 class="diwali-title">
              <span class="title-emoji">🎉</span>
              <span class="title-text">Happy Diwali Gokul!</span>
              <span class="title-emoji">🎉</span>
            </h1>
            <div class="title-decoration"></div>
          </div>
          <p class="diwali-subtitle">May this festival of lights bring you joy, prosperity, and happiness!</p>
        </section>

        <!-- Main Message -->
        <section class="diwali-message">
          <div class="card message-card">
            <h3>🪔 Wishing You a Bright Diwali! 🪔</h3>
            <p>May the divine light of Diwali spread into your life and bring you peace, prosperity, and happiness. May this festival of lights illuminate your path and fill your home with warmth and joy.</p>
          </div>
        </section>

        <!-- Memories Section -->
        <section class="memories-section">
          <div class="card memories-card">
            <h3>💝 Let's Rekindle Our Sweet Memories 💝</h3>
            <p>As we celebrate this beautiful festival of lights, let's take a moment to cherish all the wonderful memories we've created together. Each moment, each laugh, each shared experience has made our bond stronger and more precious.</p>
            
            <div class="heartfelt-message">
              <p>I know we have grown apart, and I deeply regret what ever happened between us. I would love to be your friend again and miss you so much. This Diwali, I hope we can start fresh and rebuild the beautiful friendship we once had.</p>
            </div>
            
            <div class="memories-link-container">
              <a 
                href="https://photos.app.goo.gl/MXbQbwRVNsQiScPf7" 
                target="_blank" 
                rel="noopener noreferrer"
                class="btn btn-primary memories-link"
              >
                📸 Click here to revisit our beautiful memories 📸
              </a>
            </div>
            <p class="memories-subtext">Let these photos remind us of all the joy, laughter, and love we've shared together. Happy Diwali, and here's to many more beautiful memories! ✨</p>
          </div>
        </section>

        <!-- Chocolates Section -->
        <section class="chocolates-section">
          <div class="card chocolates-card">
            <h3>🍫 Sweet Diwali Surprise from India to Germany! 🍫</h3>
            <div class="chocolates-content">
              <div class="chocolates-text">
                <p>Even though we're miles apart - you in Germany 🇩🇪 and me in India 🇮🇳 - distance can't stop us from celebrating Diwali together! I've sent you some delicious chocolates as Diwali sweets to make your celebration extra special.</p>
                <p>May these chocolates bring you the sweetness of our friendship and the warmth of Diwali celebrations, no matter where you are in the world!</p>
              </div>
              <div class="chocolates-emoji">
                <div class="floating-chocolate">🍫</div>
                <div class="floating-chocolate">🍬</div>
                <div class="floating-chocolate">🍭</div>
                <div class="floating-chocolate">🍪</div>
                <div class="floating-chocolate">🧁</div>
              </div>
            </div>
            <div class="distance-message">
              <p>💫 From India to Germany with love this Diwali! 💫</p>
            </div>
          </div>
        </section>

        <!-- Diwali Quotes -->
        <section class="diwali-quotes">
          <div class="quotes-grid">
            <div class="card quote-card">
              <p>"Diwali is the time to celebrate the victory of light over darkness, good over evil, and knowledge over ignorance."</p>
            </div>
            <div class="card quote-card">
              <p>"May the glow of a thousand diyas illuminate your life with endless joy, prosperity, and health."</p>
            </div>
          </div>
        </section>

        <!-- Handmade Card Section -->
        <section class="handmade-card-section">
          <div class="card handmade-card">
            <div class="card-icon">💌</div>
            <h3>💝 A Special Handmade Card 💝</h3>
            <p>I've created a beautiful handmade Diwali card just for you! It's filled with love, memories, and all the warmth of this festive season.</p>
            <p class="card-invitation">(You should only say if its beautiful 😜)</p>
            <p class="card-invitation">Let me know if you'd like to see it! I'd be so happy to share it with you. 💕</p>
          </div>
        </section>

        <!-- Diwali Traditions -->
        <section class="diwali-traditions">
          <h2>🎊 Diwali Traditions 🎊</h2>
          <div class="tradition-grid">
            <div class="card tradition-item">
              <div class="tradition-icon">🪔</div>
              <h3>Lighting Diyas</h3>
              <p>You are my diya, my light, my source of joy and happiness. I miss you so much and I hope you are doing well.</p>
            </div>
            <div class="card tradition-item">
              <div class="tradition-icon">🎆</div>
              <h3>Fireworks</h3>
              <p>Celebrating with colorful fireworks and sparklers, Life becomes colorful and beautiful when you are around.</p>
            </div>
            <div class="card tradition-item">
              <div class="tradition-icon">🍬</div>
              <h3>Sweet Treats</h3>
              <p>Sharing delicious sweets with family and friends, Sharing sweets with you is the best thing that I can do.</p>
            </div>
            <div class="card tradition-item">
              <div class="tradition-icon">👗</div>
              <h3>New Clothes</h3>
              <p>Wearing new traditional clothes for the celebration, Wearing new clothes with you is what i love the most, Hope you like the shirt I sent. </p>
            </div>
          </div>
        </section>

        <!-- Celebration Section -->
        <section class="celebration-section">
          <h2>🌟 Let's Celebrate Together! 🌟</h2>
          <div class="celebration-animation">
            <div class="dancing-emoji">💃</div>
            <div class="dancing-emoji">🕺</div>
            <div class="dancing-emoji">🎭</div>
            <div class="dancing-emoji">🎪</div>
          </div>
        </section>

        <!-- Signature Section -->
        <section class="signature-section">
          <div class="signature-container">
            <p class="signature-text">with loads of love,</p>
            <p class="signature-name">Vidya</p>
            <div class="signature-hearts">
              <span class="heart">💖</span>
              <span class="heart">💕</span>
              <span class="heart">💗</span>
              <span class="heart">💝</span>
              <span class="heart">💖</span>
            </div>
          </div>
        </section>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted } from 'vue'

const password = ref('')
const isAuthenticated = ref(false)
const errorMessage = ref('')

const checkPassword = () => {
  if (password.value.toLowerCase() === 'boss' || password.value.toLowerCase() === 'babe' || password.value.toLowerCase() === 'paapi' || password.value.toLowerCase() === 'purush') {
    isAuthenticated.value = true
    errorMessage.value = ''
  } else {
    errorMessage.value = 'Incorrect password. Please try again.'
    password.value = ''
  }
}


onMounted(() => {
  // Add some sparkle effect
  document.title = 'Happy Diwali Gokul! 🪔'
  
  // Track visit to Google Sheets
  trackVisit()
})

const trackVisit = async () => {
  try {
    // Get visitor information
    const timestamp = new Date().toISOString()
    const location = navigator.language || 'Unknown'
    const ip = await getVisitorIP()
    
    // Create a simple tracking entry
    const trackingData = {
      timestamp: timestamp,
      location: location,
      ip: ip,
      userAgent: navigator.userAgent,
      referrer: document.referrer || 'Direct',
      url: window.location.href
    }
    
    // Store in localStorage for debugging
    const existingVisits = JSON.parse(localStorage.getItem('gokulVisits') || '[]')
    existingVisits.push(trackingData)
    localStorage.setItem('gokulVisits', JSON.stringify(existingVisits))
    
    // Send to Google Sheets via Apps Script
    await sendToGoogleSheets(trackingData)
    
  } catch (error) {
    console.log('Visit tracking failed:', error)
    // Silently fail - don't interrupt user experience
  }
}

const sendToGoogleSheets = async (data: any) => {
  try {
    // Your actual Google Apps Script URL
    const scriptUrl = 'https://script.google.com/macros/s/AKfycbxr0IVnvNWa977d0qZMWN8i83CBHIX9okd8PhcFQx8_xTwGHMUgyZN_oo-vhyEkeksi/exec'
    
    // Send data via POST request with FormData
    const formData = new FormData()
    formData.append('timestamp', data.timestamp)
    formData.append('location', data.location)
    formData.append('ip', data.ip)
    formData.append('userAgent', data.userAgent)
    formData.append('referrer', data.referrer)
    formData.append('url', data.url)
    
    await fetch(scriptUrl, {
      method: 'POST',
      mode: 'no-cors',
      body: formData
    })
    
  } catch (error) {
    console.log('Visit tracking failed:', error)
  }
}

const getVisitorIP = async () => {
  try {
    const response = await fetch('https://api.ipify.org?format=json')
    const data = await response.json()
    return data.ip
  } catch (error) {
    return 'Unknown'
  }
}
</script>

<style scoped>
/* Global reset for this component */
* {
  box-sizing: border-box;
}

.gokul-page {
  min-height: 100vh;
  position: relative;
  margin: 0;
  padding: 0;
  overflow-x: hidden;
  background: transparent;
}

/* Password Protection Styles */
.password-screen {
  min-height: 100vh;
  height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, var(--primary-color) 0%, var(--accent-color) 100%);
  position: relative;
  margin: 0;
  padding: 0;
  width: 100%;
}

.password-screen .container {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  margin: 0;
  padding: 0;
}

.password-screen::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: url('data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100"><circle cx="20" cy="20" r="2" fill="%23fff" opacity="0.1"/><circle cx="80" cy="40" r="1" fill="%23fff" opacity="0.1"/><circle cx="40" cy="80" r="1.5" fill="%23fff" opacity="0.1"/><circle cx="90" cy="90" r="1" fill="%23fff" opacity="0.1"/></svg>');
  animation: twinkle 3s infinite;
}

@keyframes twinkle {
  0%, 100% { opacity: 0.1; }
  50% { opacity: 0.3; }
}

.password-container {
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(10px);
  border-radius: var(--radius-2xl);
  padding: var(--space-12);
  text-align: center;
  box-shadow: var(--shadow-lg);
  border: 1px solid rgba(255, 255, 255, 0.2);
  max-width: 400px;
  width: 90%;
  margin: 0 auto;
  position: relative;
  z-index: 10;
}

.diwali-icon {
  font-size: 4rem;
  margin-bottom: var(--space-6);
  animation: glow 2s ease-in-out infinite alternate;
}

@keyframes glow {
  from { text-shadow: 0 0 20px var(--accent-color); }
  to { text-shadow: 0 0 30px var(--accent-color), 0 0 40px var(--accent-color); }
}

.password-container h2 {
  color: white;
  margin-bottom: var(--space-3);
  font-size: var(--font-size-3xl);
}

.password-container p {
  color: rgba(255, 255, 255, 0.8);
  margin-bottom: var(--space-8);
}

.password-clue {
  margin-bottom: var(--space-6);
  padding: var(--space-4);
  background: rgba(255, 255, 255, 0.1);
  border-radius: var(--radius-lg);
  border: 1px solid rgba(255, 255, 255, 0.2);
  backdrop-filter: blur(5px);
}

.clue-text {
  color: rgba(255, 255, 255, 0.9);
  font-size: var(--font-size-base);
  font-weight: 500;
  margin: 0;
  text-align: center;
  font-style: italic;
}

.password-input-container {
  display: flex;
  gap: var(--space-3);
  margin-bottom: var(--space-6);
  align-items: center;
  justify-content: center;
}

.password-input {
  flex: 1;
  padding: var(--space-3) var(--space-4);
  border: none;
  border-radius: var(--radius-lg);
  background: rgba(255, 255, 255, 0.2);
  color: white;
  font-size: var(--font-size-base);
  outline: none;
  backdrop-filter: blur(5px);
}

.password-input::placeholder {
  color: rgba(255, 255, 255, 0.6);
}

.error-message {
  color: #ef4444;
  font-size: var(--font-size-sm);
  margin: 0;
}

/* Diwali Content Styles */
.diwali-content {
  padding: var(--space-20) 0;
  background: var(--background-color);
}

.diwali-hero {
  text-align: center;
  margin-bottom: var(--space-16);
  position: relative;
}

.diwali-title-container {
  position: relative;
  display: inline-block;
  margin-bottom: var(--space-8);
}

.diwali-title {
  font-size: var(--font-size-4xl);
  margin: 0;
  color: var(--text-color);
  display: flex;
  align-items: center;
  justify-content: center;
  gap: var(--space-4);
  font-weight: 700;
  letter-spacing: -0.02em;
  position: relative;
  z-index: 2;
  flex-wrap: wrap;
}

.title-emoji {
  font-size: 1.2em;
  animation: bounce 2s ease-in-out infinite;
  display: inline-block;
}

.title-emoji:nth-child(3) {
  animation-delay: 0.5s;
}

.title-text {
  background: linear-gradient(135deg, var(--primary-color), var(--accent-color));
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  animation: titleGlow 3s ease-in-out infinite alternate;
  position: relative;
}

.title-text::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(135deg, var(--primary-color), var(--accent-color));
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  filter: blur(2px);
  opacity: 0.3;
  z-index: -1;
}

.title-decoration {
  position: absolute;
  bottom: -var(--space-3);
  left: 50%;
  transform: translateX(-50%);
  width: 120px;
  height: 4px;
  background: linear-gradient(90deg, var(--primary-color), var(--accent-color), var(--primary-color));
  border-radius: 2px;
  animation: decorationGlow 2s ease-in-out infinite alternate;
}

@keyframes bounce {
  0%, 20%, 50%, 80%, 100% {
    transform: translateY(0) scale(1);
  }
  40% {
    transform: translateY(-10px) scale(1.1);
  }
  60% {
    transform: translateY(-5px) scale(1.05);
  }
}

@keyframes titleGlow {
  from { 
    text-shadow: 0 0 20px rgba(37, 99, 235, 0.3);
  }
  to { 
    text-shadow: 0 0 30px rgba(37, 99, 235, 0.5), 0 0 40px rgba(245, 158, 11, 0.3);
  }
}

@keyframes decorationGlow {
  from { 
    box-shadow: 0 0 10px rgba(37, 99, 235, 0.3);
  }
  to { 
    box-shadow: 0 0 20px rgba(37, 99, 235, 0.5), 0 0 30px rgba(245, 158, 11, 0.3);
  }
}

.diwali-subtitle {
  font-size: var(--font-size-xl);
  color: var(--text-muted);
  max-width: 600px;
  margin: 0 auto;
  line-height: 1.7;
  font-weight: 400;
}

.diwali-message {
  margin-bottom: var(--space-16);
}

.message-card {
  text-align: center;
  background: linear-gradient(135deg, var(--surface-color) 0%, rgba(37, 99, 235, 0.05) 100%);
  border: 1px solid rgba(37, 99, 235, 0.2);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}

.message-card h3 {
  font-size: var(--font-size-2xl);
  margin-bottom: var(--space-6);
  color: var(--accent-color);
}

.message-card p {
  font-size: var(--font-size-lg);
  line-height: 1.7;
  color: var(--text-muted);
}

.memories-section {
  margin-bottom: var(--space-16);
}

.memories-card {
  text-align: center;
  background: linear-gradient(135deg, var(--surface-color) 0%, rgba(245, 158, 11, 0.05) 100%);
  border: 1px solid rgba(245, 158, 11, 0.2);
  position: relative;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}

.memories-card::before {
  content: '';
  position: absolute;
  top: -50%;
  left: -50%;
  width: 200%;
  height: 200%;
  background: radial-gradient(circle, rgba(245, 158, 11, 0.1) 0%, transparent 70%);
  animation: memoriesGlow 4s ease-in-out infinite;
}

@keyframes memoriesGlow {
  0%, 100% { opacity: 0.3; transform: rotate(0deg); }
  50% { opacity: 0.6; transform: rotate(180deg); }
}

.memories-card h3 {
  font-size: var(--font-size-2xl);
  margin-bottom: var(--space-6);
  color: var(--accent-color);
  position: relative;
  z-index: 1;
}

.memories-card p {
  font-size: var(--font-size-lg);
  line-height: 1.7;
  color: var(--text-muted);
  position: relative;
  z-index: 1;
  margin-bottom: var(--space-8);
}

.memories-link-container {
  margin: var(--space-8) 0;
  position: relative;
  z-index: 1;
}

.memories-link {
  animation: gradientShift 3s ease infinite;
}

@keyframes gradientShift {
  0% { background-position: 0% 50%; }
  50% { background-position: 100% 50%; }
  100% { background-position: 0% 50%; }
}

.heartfelt-message {
  background: var(--background-color);
  border-radius: var(--radius-lg);
  padding: var(--space-6);
  margin: var(--space-6) 0;
  border-left: 4px solid var(--accent-color);
  position: relative;
  z-index: 1;
}

.heartfelt-message p {
  font-size: var(--font-size-lg);
  line-height: 1.7;
  color: var(--text-color);
  font-style: italic;
  margin: 0;
  text-align: center;
}

.memories-subtext {
  font-size: var(--font-size-lg);
  line-height: 1.7;
  color: var(--accent-color);
  position: relative;
  z-index: 1;
  font-style: italic;
}

.chocolates-section {
  margin-bottom: var(--space-16);
}

.chocolates-card {
  text-align: center;
  background: linear-gradient(135deg, var(--surface-color) 0%, rgba(245, 158, 11, 0.1) 100%);
  border: 1px solid rgba(245, 158, 11, 0.3);
  position: relative;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}

.chocolates-card::before {
  content: '';
  position: absolute;
  top: -50%;
  left: -50%;
  width: 200%;
  height: 200%;
  background: radial-gradient(circle, rgba(245, 158, 11, 0.1) 0%, transparent 70%);
  animation: chocolatesGlow 5s ease-in-out infinite;
}

@keyframes chocolatesGlow {
  0%, 100% { opacity: 0.3; transform: rotate(0deg); }
  50% { opacity: 0.6; transform: rotate(180deg); }
}

.chocolates-card h3 {
  font-size: var(--font-size-2xl);
  margin-bottom: var(--space-8);
  color: var(--accent-color);
  position: relative;
  z-index: 1;
}

.chocolates-content {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--space-8);
  margin-bottom: var(--space-8);
  position: relative;
  z-index: 1;
}

.chocolates-text {
  flex: 1;
  text-align: left;
}

.chocolates-text p {
  font-size: var(--font-size-lg);
  line-height: 1.7;
  color: var(--text-muted);
  margin-bottom: var(--space-4);
}

.chocolates-emoji {
  display: flex;
  flex-direction: column;
  gap: var(--space-4);
  align-items: center;
}

.floating-chocolate {
  font-size: 2.5rem;
  animation: chocolateFloat 3s ease-in-out infinite;
}

.floating-chocolate:nth-child(1) { animation-delay: 0s; }
.floating-chocolate:nth-child(2) { animation-delay: 0.6s; }
.floating-chocolate:nth-child(3) { animation-delay: 1.2s; }
.floating-chocolate:nth-child(4) { animation-delay: 1.8s; }
.floating-chocolate:nth-child(5) { animation-delay: 2.4s; }

@keyframes chocolateFloat {
  0%, 100% {
    transform: translateY(0px) rotate(0deg);
  }
  50% {
    transform: translateY(-15px) rotate(10deg);
  }
}

.distance-message {
  position: relative;
  z-index: 1;
  margin-top: var(--space-6);
}

.distance-message p {
  font-size: var(--font-size-xl);
  font-weight: bold;
  color: var(--accent-color);
  animation: pulse 2s ease-in-out infinite;
}

@keyframes pulse {
  0%, 100% { transform: scale(1); }
  50% { transform: scale(1.05); }
}

.diwali-quotes {
  margin-bottom: var(--space-16);
}

/* Handmade Card Section */
.handmade-card-section {
  margin-bottom: var(--space-16);
}

.handmade-card {
  text-align: center;
  background: linear-gradient(135deg, var(--surface-color) 0%, rgba(245, 158, 11, 0.05) 100%);
  border: 1px solid rgba(245, 158, 11, 0.2);
  position: relative;
  overflow: hidden;
  max-width: 600px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}

.handmade-card::before {
  content: '';
  position: absolute;
  top: -50%;
  left: -50%;
  width: 200%;
  height: 200%;
  background: radial-gradient(circle, rgba(245, 158, 11, 0.1) 0%, transparent 70%);
  animation: cardGlow 4s ease-in-out infinite;
}

@keyframes cardGlow {
  0%, 100% { opacity: 0.3; transform: rotate(0deg); }
  50% { opacity: 0.6; transform: rotate(180deg); }
}

.card-icon {
  font-size: 3rem;
  margin-bottom: var(--space-4);
  animation: cardFloat 3s ease-in-out infinite;
  display: block;
}

@keyframes cardFloat {
  0%, 100% {
    transform: translateY(0px) rotate(0deg);
  }
  50% {
    transform: translateY(-8px) rotate(5deg);
  }
}

.handmade-card h3 {
  font-size: var(--font-size-2xl);
  margin-bottom: var(--space-6);
  color: var(--accent-color);
  position: relative;
  z-index: 1;
}

.handmade-card p {
  font-size: var(--font-size-lg);
  line-height: 1.7;
  color: var(--text-muted);
  margin-bottom: var(--space-4);
  position: relative;
  z-index: 1;
}

.card-invitation {
  font-size: var(--font-size-lg);
  color: var(--primary-color);
  font-weight: 500;
  font-style: italic;
  position: relative;
  z-index: 1;
}

.quotes-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: var(--space-8);
}

.quote-card {
  text-align: center;
  background: linear-gradient(135deg, var(--surface-color) 0%, rgba(37, 99, 235, 0.05) 100%);
  border: 1px solid rgba(37, 99, 235, 0.2);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}

.quote-card p {
  font-style: italic;
  font-size: var(--font-size-lg);
  line-height: 1.7;
  color: var(--accent-color);
}

.diwali-traditions {
  margin-bottom: var(--space-16);
}

.diwali-traditions h2 {
  text-align: center;
  font-size: var(--font-size-3xl);
  margin-bottom: var(--space-12);
  color: var(--accent-color);
}

.tradition-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: var(--space-8);
}

.tradition-item {
  text-align: center;
  background: linear-gradient(135deg, var(--surface-color) 0%, rgba(245, 158, 11, 0.05) 100%);
  border: 1px solid rgba(245, 158, 11, 0.2);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}

.tradition-icon {
  font-size: 3rem;
  margin-bottom: var(--space-4);
  display: block;
}

.tradition-item h3 {
  font-size: var(--font-size-xl);
  margin-bottom: var(--space-4);
  color: var(--accent-color);
}

.tradition-item p {
  line-height: 1.7;
  color: var(--text-muted);
  font-size: var(--font-size-lg);
}

.celebration-section {
  text-align: center;
}

.celebration-section h2 {
  font-size: var(--font-size-3xl);
  margin-bottom: var(--space-12);
  color: var(--accent-color);
}

.celebration-animation {
  display: flex;
  justify-content: center;
  gap: var(--space-8);
  flex-wrap: wrap;
}

.dancing-emoji {
  font-size: 3rem;
  animation: dance 2s ease-in-out infinite;
}

@keyframes dance {
  0%, 100% {
    transform: rotate(0deg) scale(1);
  }
  25% {
    transform: rotate(-10deg) scale(1.1);
  }
  75% {
    transform: rotate(10deg) scale(1.1);
  }
}

.dancing-emoji:nth-child(2) {
  animation-delay: 0.5s;
}

.dancing-emoji:nth-child(3) {
  animation-delay: 1s;
}

.dancing-emoji:nth-child(4) {
  animation-delay: 1.5s;
}

/* Signature Section */
.signature-section {
  margin-top: var(--space-20);
  padding: var(--space-16) 0;
  text-align: center;
  background: linear-gradient(135deg, var(--surface-color) 0%, rgba(37, 99, 235, 0.05) 100%);
  border-radius: var(--radius-2xl);
  border: 1px solid rgba(37, 99, 235, 0.1);
  position: relative;
  overflow: hidden;
}

.signature-section::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: radial-gradient(circle at 50% 50%, rgba(245, 158, 11, 0.1) 0%, transparent 70%);
  animation: signatureGlow 4s ease-in-out infinite;
}

@keyframes signatureGlow {
  0%, 100% { opacity: 0.3; }
  50% { opacity: 0.6; }
}

.signature-container {
  position: relative;
  z-index: 1;
}

.signature-text {
  font-size: var(--font-size-xl);
  color: var(--text-muted);
  margin-bottom: var(--space-2);
  font-style: italic;
  font-weight: 300;
}

.signature-name {
  font-size: var(--font-size-3xl);
  color: var(--primary-color);
  margin-bottom: var(--space-6);
  font-weight: 600;
  letter-spacing: 0.05em;
  background: linear-gradient(135deg, var(--primary-color), var(--accent-color));
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  animation: signatureGlow 3s ease-in-out infinite alternate;
}

.signature-hearts {
  display: flex;
  justify-content: center;
  gap: var(--space-3);
  flex-wrap: wrap;
}

.heart {
  font-size: 1.5rem;
  animation: heartFloat 2s ease-in-out infinite;
  display: inline-block;
}

.heart:nth-child(1) { animation-delay: 0s; }
.heart:nth-child(2) { animation-delay: 0.4s; }
.heart:nth-child(3) { animation-delay: 0.8s; }
.heart:nth-child(4) { animation-delay: 1.2s; }
.heart:nth-child(5) { animation-delay: 1.6s; }

@keyframes heartFloat {
  0%, 100% {
    transform: translateY(0px) scale(1);
  }
  50% {
    transform: translateY(-8px) scale(1.1);
  }
}

/* Mobile responsiveness */
@media (max-width: 768px) {
  /* .gokul-page {
    padding: var(--space-8) 0;
  } */
  
  .diwali-title {
    font-size: var(--font-size-3xl);
    flex-direction: row;
    gap: var(--space-3);
    justify-content: center;
    text-align: center;
  }
  
  .title-emoji {
    font-size: 1.2em;
  }
  
  .title-decoration {
    width: 80px;
    height: 3px;
  }
  
  .diwali-subtitle {
    font-size: var(--font-size-lg);
  }
  
  .password-container {
    padding: var(--space-8) var(--space-6);
  }
  
  .password-clue {
    padding: var(--space-3);
    margin-bottom: var(--space-4);
  }
  
  .password-input-container {
    flex-direction: column;
    gap: var(--space-4);
  }
  
  .password-input {
    width: 100%;
  }
  
  .clue-text {
    font-size: var(--font-size-sm);
  }
  
  .diwali-content {
    padding: var(--space-8) 0;
  }
  
  .diwali-hero {
    margin-bottom: var(--space-12);
  }
  
  .diwali-message,
  .memories-section,
  .chocolates-section,
  .diwali-quotes,
  .handmade-card-section,
  .diwali-traditions {
    margin-bottom: var(--space-12);
  }
  
  .handmade-card h3 {
    font-size: var(--font-size-xl);
  }
  
  .handmade-card p {
    font-size: var(--font-size-base);
  }
  
  .card-invitation {
    font-size: var(--font-size-base);
  }
  
  .memories-card h3,
  .chocolates-card h3 {
    font-size: var(--font-size-xl);
  }
  
  .memories-link {
    font-size: var(--font-size-base);
  }
  
  .heartfelt-message {
    padding: var(--space-4);
  }
  
  .heartfelt-message p {
    font-size: var(--font-size-base);
  }
  
  .chocolates-content {
    flex-direction: column;
    gap: var(--space-6);
  }
  
  .chocolates-text {
    text-align: center;
  }
  
  .chocolates-emoji {
    flex-direction: row;
    justify-content: center;
    flex-wrap: wrap;
  }
  
  .floating-chocolate {
    font-size: 2rem;
  }
  
  .distance-message p {
    font-size: var(--font-size-lg);
  }
  
  .quotes-grid {
    grid-template-columns: 1fr;
    gap: var(--space-6);
  }
  
  .tradition-grid {
    grid-template-columns: 1fr;
    gap: var(--space-6);
  }
  
  .celebration-animation {
    gap: var(--space-6);
  }
  
  .dancing-emoji {
    font-size: 2.5rem;
  }
  
  .diwali-traditions h2,
  .celebration-section h2 {
    font-size: var(--font-size-2xl);
    margin-bottom: var(--space-8);
  }
  
  .signature-section {
    margin-top: var(--space-12);
    padding: var(--space-12) 0;
  }
  
  .signature-text {
    font-size: var(--font-size-lg);
  }
  
  .signature-name {
    font-size: var(--font-size-2xl);
  }
  
  .heart {
    font-size: 1.25rem;
  }
}
</style>
